import { useProductPageStore } from '../../contexts/ProductPageStore'
import { getSegmentImageUrl } from '../../utils/trackingEvents'
import styles from './mobileStickyAtc.module.scss'

import React, { useContext, useEffect, useRef, useState } from 'react'

import ErrorBoundary from 'Clutch/UtilityComponents/ErrorBoundary/ErrorBoundary'
import ClassNames from 'classnames'

import { GetUserGuid } from 'Clutch/Utilities/CookieUtilties/UserCookieUtilities'
import { getSuperMarketAffinity } from 'Clutch/Utilities/Instrumentation/SegmentUtilities/segmentFunctions'

import { AddToCartExperienceContext } from 'Clutch/Contexts/AddToCartExperienceContext/AddToCartExperienceContext'

import { useLazyFeatureFlag } from 'Clutch/Hooks/useFeatureFlag/useFeatureFlag'
import useIsMobile from 'Clutch/Hooks/useIsMobile/useIsMobile'

import Button from 'Clutch/Molecules/Button'

import Panel from 'Clutch/Atoms/Panel/Panel'

const STICKY_ATC_HEIGHT = '56px'
const STICKY_ATC_HEIGHT_PWA = '68px'

const MobileStickyFooter = ({ triggerId, testing }) => {
  const [isVisible, setVisibility] = useState(false)
  const [lastScrollY, setLastScrollY] = useState(0)
  const lastCallTime = useRef(0)
  const isMobile = useIsMobile()
  let enabled = useLazyFeatureFlag('PDP_Sticky_ATC_Enabled', isMobile && isVisible)

  if (testing) {
    enabled = true
  }

  const handleScroll = () => {
    const now = Date.now()

    // Only execute if at least 1 second has passed since last call
    if (now - lastCallTime.current < 500) {
      return
    }

    lastCallTime.current = now

    const currentScrollY = window.pageYOffset
    const triggerElement = document.getElementById(triggerId)

    if (triggerElement) {
      const triggerOffset = triggerElement.offsetTop
      const isBelowTrigger = triggerOffset < currentScrollY

      const scrollingUp = currentScrollY < lastScrollY
      setLastScrollY(currentScrollY)

      // If enabled, hide on scroll up. If disabled, only check trigger position
      if (enabled) {
        if (isBelowTrigger && !scrollingUp) {
          setVisibility(true)
        } else {
          setVisibility(false)
        }
      } else {
        setVisibility(isBelowTrigger)
      }
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [lastScrollY, enabled])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return

    const isPwa = window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator?.standalone === true
    const isIos = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent)
    const height = isPwa && isIos ? STICKY_ATC_HEIGHT_PWA : STICKY_ATC_HEIGHT

    if (isMobile && isVisible) {
      document.documentElement?.style?.setProperty('--sticky-atc-offset', height)
    } else {
      document.documentElement?.style?.setProperty('--sticky-atc-offset', '0px')
    }
  }, [isVisible, isMobile])

  const superMarketAffinity = useProductPageStore((x) => x.superMarketAffinity)
  const skuVariantNumber = useProductPageStore((x) => x.skuVariantNumber)
  const skuBaseNumber = useProductPageStore((x) => x.skuBaseNumber)
  const quantityRequested = useProductPageStore((x) => x.quantityRequested)
  const updateQuantityRequested = useProductPageStore((x) => x.updateQuantityRequested)
  const title = useProductPageStore((x) => x.title)
  const images = useProductPageStore((x) => x.images)
  const productImages = useProductPageStore((x) => x.productImages)
  const displayImageUrl = useProductPageStore((x) => x.displayImageUrl)
  const segmentCategorization = useProductPageStore((x) => x.segmentCategorization)
  const brand = useProductPageStore((x) => x.brand)
  const retailLowPrice = useProductPageStore((x) => x.retailLowPrice)
  const productPageUrl = useProductPageStore((x) => x.productPageUrl)
  const isPurchaseable = useProductPageStore((x) => x.isPurchaseable)
  const availabilityEnum = useProductPageStore((x) => x.availabilityEnum)
  const reviewCount = useProductPageStore((x) => x.reviewCount)
  const rating = useProductPageStore((x) => x.rating)
  const isIndexOptionKit = useProductPageStore((x) => x.isIndexOptionKit)
  const errorIndexOptions = useProductPageStore((x) => x.errorIndexOptions)
  const addToCartExperienceContext = useContext(AddToCartExperienceContext)

  const item = {
    skuVariantNumber: skuVariantNumber,
    skuBaseNumber: skuBaseNumber,
    quantityRequested: parseInt(quantityRequested),
    title: title,
    images: images,
    segmentCategory: segmentCategorization,
    brand: brand?.brandName,
    price: retailLowPrice,
    url: productPageUrl,
  }

  return (
    <ErrorBoundary>
      {isPurchaseable ? (
        <Panel
          className={ClassNames(styles.atc_container, !isVisible ? styles.slide : false)}
          id={`pdp_atc_sticky_container`}
          inverseTheme
        >
          <Button
            inline
            intent={'positive'}
            size={'small'}
            text={'Add to Cart'}
            icon={'add_to_cart'}
            className={ClassNames(styles.atc_button)}
            data-testid={'mobile_sticky_atc_btn'}
            data-cnstrc-btn={'add_to_cart'}
            segmentEvent={{
              event: 'Product Add Started',
              properties: {
                cart_id: GetUserGuid(),
                product_id: skuBaseNumber,
                sku: skuBaseNumber,
                name: title,
                variant: skuVariantNumber,
                quantity: quantityRequested,
                brand: brand ? brand.brandName || '' : '',
                category: segmentCategorization,
                price: retailLowPrice,
                url: productPageUrl,
                image_url: getSegmentImageUrl({ images, productImages, displayImageUrl }),
                availability: availabilityEnum,
                rating: reviewCount ? rating : 0,
                reviews: reviewCount,
                audience_affinity: getSuperMarketAffinity({
                  superMarketAffinity,
                }),
              },
            }}
            onClick={(e) => {
              e.preventDefault()

              try {
                analytics.track('Clicked', {
                  element: `mobile_sticky_atc_btn`,
                })
              } catch (err) {
                console.error('Error in MobileStickyFooter onClick:', err)
              }

              if (skuVariantNumber) {
                isIndexOptionKit
                  ? addToCartExperienceContext.showKitDetailsDrawer()
                  : addToCartExperienceContext.addToCart([item]).then((success) => {
                      if (success) {
                        updateQuantityRequested(1)
                      }
                    })
              } else {
                errorIndexOptions()
              }
            }}
          />
        </Panel>
      ) : (
        false
      )}
    </ErrorBoundary>
  )
}

export default MobileStickyFooter
